import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";

import isCordova from "../../../helpers/cordova-check";
import isToken from "../../../helpers/token-check";
import { useMembershipActive } from "../../../hooks/usePurchasedProducts";
import { isAndroid, isIos } from "../../../helpers/device-check";
import { menuItems } from "../data/drawerList3";

import placeholderIcon from "../../../images/icons/side-menu-placeholder.svg";
import settingIcon from "../../../images/icons/sidebar-setting.svg";
import supportIcon from "../../../images/icons/sidebar-support.svg";
import appIcon from "../../../images/icons/sidebar-app.svg";
import podcastsIcon from "../../../images/icons/sidebar-podcasts.svg";
import logoutIcon from "../../../images/icons/sidebar-logout.svg";
import google from "../../../images/summit/Google_Cloud_Partner_outline_horizontal.png";

const baseMenuTextStyle = {
  fontFamily: "Open Sans",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "21px",
  letterSpacing: "-0.02em",
};

const subLabelTextStyle = {
  fontFamily: "Open Sans",
  fontSize: 12,
  lineHeight: "18px",
  letterSpacing: "-0.02em",
  color: "rgb(24, 143, 255)",
  fontWeight: 400,
  marginLeft: 2,
};

const formatYear = (date) => {
  const year = new Date(date).getFullYear();
  return Number.isNaN(year) ? null : year;
};

const formatMembershipLabel = (text, createdAt) => {
  const year = formatYear(createdAt);
  const isFreeUser = !text || text === "Free User";
  const label = isFreeUser ? (year ? `Since ${year}` : "Free User") : text;
  if (label === "Membership" || label === "Premium Membership") {
    return "Premium Membership";
  }
  if (
    label.startsWith("Membership expires ") ||
    label.startsWith("Premium Membership expires")
  ) {
    return "Premium Membership";
  }
  return label;
};

const sectionTextStyle = {
  fontFamily: "Open Sans",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
  letterSpacing: "-0.02em",
  textTransform: "uppercase",
};

const MOBILE_BREAKPOINT = 1280;
export const EXPANDED_DRAWER_WIDTH = 280;
export const COLLAPSED_DRAWER_WIDTH = 48;
const MENU_SECTION_ICONS = {};

/** Scrollable areas: keep scroll, hide scrollbar (Chrome / Firefox / legacy Edge). */
const DRAWER_SCROLL_NO_VISIBLE_BAR =
  "overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden";

/** Drop items marked hideInCordova when running inside the native app shell. */
function filterMenuItemsForCordova(items) {
  if (!isCordova() || !Array.isArray(items)) return items;
  return items
    .filter((item) => !item?.hideInCordova)
    .map((item) => {
      if (!item?.children?.length) return item;
      const children = filterMenuItemsForCordova(item.children);
      return { ...item, children };
    });
}

const SideDrawerNew = (props) => {
  const { isSideDrawerOpen, setSideDrawerOpen, isDrawerPermanent, user } =
    props;
  const [expandedMenu, setExpandedMenu] = useState({});
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.innerWidth <= MOBILE_BREAKPOINT
      : true
  );
  const history = useHistory();
  const { pathname, hash } = useLocation();
  const [menuRevision, setMenuRevision] = useState(0);
  useEffect(() => {
    const bumpMenu = () => setMenuRevision((n) => n + 1);
    document.addEventListener("deviceready", bumpMenu, false);
    if (window.cordova) {
      bumpMenu();
    }
    return () => document.removeEventListener("deviceready", bumpMenu, false);
  }, []);
  const effectiveMenuItems = useMemo(
    () => filterMenuItemsForCordova(menuItems),
    [menuRevision]
  );
  const isDesktop = !isMobile;
  const isOverlayDrawer = isMobile || !isDrawerPermanent;
  const currentDrawerWidth =
    isDesktop && !isSideDrawerOpen
      ? COLLAPSED_DRAWER_WIDTH
      : EXPANDED_DRAWER_WIDTH;

  const handleUpgradeRenewClick = useCallback(() => {
    if (props?.user?.UpgradeOrRenewButtonTextURL) {
      window.open(props.user.UpgradeOrRenewButtonTextURL, "_blank");
      return;
    }
    history.push("/pricing-premium");
  }, [history, props?.user?.UpgradeOrRenewButtonTextURL]);

  const { isActive: hasPaidMembership } = useMembershipActive();

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const getItemKey = useCallback(
    (item, index, parentKey = "root") =>
      `${parentKey}-${item?.id || item?.to || item?.label || "item"}-${index}`,
    []
  );

  const getRouteParts = useCallback((route) => {
    if (!route || route === "#") return { path: "", routeHash: "" };
    if (route.startsWith("http")) {
      try {
        const parsedUrl = new URL(route);
        return {
          path: parsedUrl.pathname || "",
          routeHash: parsedUrl.hash || "",
        };
      } catch (error) {
        return { path: "", routeHash: "" };
      }
    }
    const [pathWithQuery = "", rawHash = ""] = route.split("#");
    return {
      path: pathWithQuery.split("?")[0] || pathWithQuery,
      routeHash: rawHash ? `#${rawHash}` : "",
    };
  }, []);

  const isRouteActive = useCallback(
    (route) => {
      const { path: routePath, routeHash } = getRouteParts(route);
      if (!routePath) return false;
      const isPathMatch =
        routePath === "/"
          ? pathname === "/"
          : routePath === "/property/land"
            ? pathname === routePath ||
              pathname.startsWith(`${routePath}/`) ||
              /^\/property\/[^/]+\/(land|agents|launches|decision)$/.test(pathname)
            : pathname === routePath || pathname.startsWith(`${routePath}/`);

      if (!isPathMatch) return false;
      if (!routeHash) return true;
      return hash === routeHash;
    },
    [getRouteParts, hash, pathname]
  );

  const isAnyRouteActive = useCallback(
    (routes = []) => routes.some((route) => isRouteActive(route)),
    [isRouteActive]
  );

  const isItemSelected = useCallback(
    (item) =>
      isAnyRouteActive([
        item?.to,
        ...(Array.isArray(item?.matchRoutes) ? item.matchRoutes : []),
      ]),
    [isAnyRouteActive]
  );

  const isItemExternalLink = useCallback(
    (item) => item?.to?.includes("https") || item?.openInNewTab,
    []
  );

  const hasSelectedDescendant = useCallback(
    (item) => {
      if (!item?.children?.length) return false;
      return item.children.some(
        (child) => isItemSelected(child) || hasSelectedDescendant(child)
      );
    },
    [isItemSelected]
  );

  useEffect(() => {
    const nextExpanded = {};

    const collectExpanded = (items, parentKey = "root") => {
      items?.forEach((item, index) => {
        const key = getItemKey(item, index, parentKey);
        if (item?.children?.length) {
          if (hasSelectedDescendant(item)) {
            nextExpanded[key] = true;
          }
          collectExpanded(item.children, key);
        }
      });
    };

    collectExpanded(effectiveMenuItems);
    setExpandedMenu((prev) => ({ ...prev, ...nextExpanded }));
  }, [effectiveMenuItems, getItemKey, hasSelectedDescendant, hash, pathname]);

  const handleListItemClick = useCallback(
    (item) => {
      const closeDrawerIfOverlay = () => {
        if (isOverlayDrawer) {
          setSideDrawerOpen(false);
        }
      };

      if (item?.actionType === "upgrade") {
        handleUpgradeRenewClick();
        closeDrawerIfOverlay();
        return;
      }

      if (isCordova() && item?.id === "home") {
        return;
      }

      if (!item?.to || item?.to === "#") {
        return;
      }

      closeDrawerIfOverlay();
      if (isItemExternalLink(item)) {
        const isInternalRoute =
          item.to?.startsWith("/") && !item.to?.startsWith("http");
        if (isCordova() && isInternalRoute) {
          history.push(item.to);
        } else {
          const url = item.to?.startsWith("http")
            ? item.to
            : `${window.location.origin}${item.to}`;
          if (item.openInNewTab === false) {
            window.location.href = url;
          } else {
            window.open(url, "_blank");
          }
        }
      } else if (item.to !== "") history.push(item.to);
      else return;
    },
    [
      handleUpgradeRenewClick,
      history,
      isItemExternalLink,
      isOverlayDrawer,
      setSideDrawerOpen,
    ]
  );

  const toggleGroup = useCallback((key) => {
    setExpandedMenu((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleOpenApp = () => {
    if (isCordova()) {
      let appId = "";
      if (isAndroid()) appId = "com.spiking.spike.android";
      if (isIos()) appId = "1081721327";

      window.LaunchReview.launch(
        () => {
          console.log("Successfully launched store app");
        },
        (error) => {
          console.log("Error launching store app: " + error);
        },
        appId
      );
    } else {
      window.open("https://spiking.com/app", "_blank");
    }
  };

  const handleLogout = () => {
    if (isCordova()) {
      window.cordova.plugins.GoogleSignInPlugin.signOut(
        function (success) {
          console.log(success);
          history.push("/logout");
        },
        function (error) {
          console.error(error);
        }
      );
    } else history.push("/logout");
  };

  const renderNode = useCallback(
    (item, level = 0, parentKey = "root", index = 0) => {
      if (item?.requiresGuest && isToken()) return null;
      if (item?.requiresAuth && !isToken()) return null;

      if (item?.isDivider) {
        return (
          <div
            key={`${parentKey}-divider-${index}`}
            className="mx-3 my-3 h-px bg-[#555555]"
          />
        );
      }

      const hasChildren = item?.children?.length > 0;
      const key = getItemKey(item, index, parentKey);
      const isDirectSelected = isItemSelected(item);
      const hasActiveChild = hasSelectedDescendant(item);
      const isActive = isDirectSelected || hasActiveChild;
      const isExpanded = hasChildren ? Boolean(expandedMenu[key]) : false;
      const isSectionHeader = Boolean(item?.isSectionHeader);
      const isTopSection = level === 0 && Boolean(item?.isSection);
      const itemPaddingLeft = level >= 2 ? 28 : 12;

      const iconSrc =
        item?.iconImage || MENU_SECTION_ICONS[item?.label] || placeholderIcon;
      const iconNode = item?.iconImage ? (
        <img
          src={item.iconImage}
          alt={item.label || ""}
          className="h-4 w-4 object-contain flex-shrink-0"
        />
      ) : item?.showIcon ? (
        <img
          src={iconSrc}
          alt=""
          className="h-4 w-4 object-contain flex-shrink-0"
        />
      ) : item?.icon ? (
        <span className="inline-flex h-4 w-4 items-center justify-center text-white/90">
          {item.icon}
        </span>
      ) : null;

      return (
        <div key={key}>
          {isTopSection ? (
            item?.to && item?.to !== "#" ? (
              <button
                type="button"
                onClick={() => handleListItemClick(item)}
                className={`flex w-full items-center gap-2 px-3 pb-2 pt-3 text-left hover:bg-[#111111] ${
                  item?.sectionVariant === "muted"
                    ? "text-[#ACACAC]"
                    : "text-white"
                }`}
              >
                {iconNode}
                <span
                  style={
                    item?.sectionVariant === "muted"
                      ? sectionTextStyle
                      : baseMenuTextStyle
                  }
                  className={
                    item?.sectionVariant === "muted" ? "" : "font-semibold"
                  }
                >
                  {item?.label}
                </span>
              </button>
            ) : (
              <div
                className={`flex items-center gap-2 px-3 pb-2 pt-3 ${
                  item?.sectionVariant === "muted"
                    ? "text-[#ACACAC]"
                    : "text-white"
                }`}
              >
                {iconNode}
                <span
                  style={
                    item?.sectionVariant === "muted"
                      ? sectionTextStyle
                      : baseMenuTextStyle
                  }
                  className={
                    item?.sectionVariant === "muted" ? "" : "font-semibold"
                  }
                >
                  {item?.label}
                </span>
              </div>
            )
          ) : null}

          {isSectionHeader ? (
            <div className="flex w-full items-center py-[5px] pl-3 pr-3 text-left text-[#ACACAC]">
              {item?.icon ? (
                <span className="mr-2 inline-flex h-4 w-4 items-center justify-center text-white/90">
                  {item.icon}
                </span>
              ) : null}
              <span style={sectionTextStyle}>{item?.label}</span>
            </div>
          ) : isTopSection ? null : (
            <button
              type="button"
              onClick={() =>
                hasChildren ? toggleGroup(key) : handleListItemClick(item)
              }
              className={`group relative flex w-full items-center justify-between py-[5px] pr-3 text-left transition-colors ${
                item?.highlight ? "bg-[#1b1f23]" : ""
              } ${
                isDirectSelected
                  ? "bg-[#191919]"
                  : isActive
                  ? "bg-[#111111]"
                  : "hover:bg-[#111111]"
              }`}
              style={
                item?.backgroundColor
                  ? {
                      backgroundColor: item.backgroundColor,
                      paddingLeft: itemPaddingLeft,
                    }
                  : { paddingLeft: itemPaddingLeft }
              }
            >
              {isDirectSelected ? (
                <span className="absolute left-0 top-0 h-full w-1 bg-[#FFC337]" />
              ) : null}
              <div className="flex min-w-0 flex-1 items-center gap-2">
                {iconNode}
                <div className="flex min-w-0 flex-1 items-center gap-1">
                  <span
                    style={baseMenuTextStyle}
                    className={`min-w-0 shrink truncate ${
                      isDirectSelected
                        ? "font-semibold text-[#FFC337]"
                        : isActive
                        ? "font-semibold text-white"
                        : "text-white"
                    }`}
                  >
                    {item?.label}
                    {item?.subLabel && (
                      <sup
                        style={{
                          ...subLabelTextStyle,
                          ...(isDirectSelected ? { color: "#FFC337" } : {}),
                        }}
                      >
                        {item.subLabel}
                      </sup>
                    )}
                  </span>
                  {item?.freeBadge ? (
                    <span
                      className="inline-flex shrink-0 items-center rounded-full border border-[#FF9500] px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-[#FF9500]"
                      aria-label="Free"
                    >
                      FREE
                    </span>
                  ) : null}
                </div>
              </div>
              {hasChildren ? (
                <span
                  className={`ml-2 text-[14px] leading-none text-[#ACACAC] transition-transform ${
                    isExpanded ? "rotate-45" : ""
                  }`}
                >
                  +
                </span>
              ) : null}
            </button>
          )}

          {hasChildren && isExpanded && !isTopSection && (
            <div>
              {item.children.map((child, childIndex) =>
                renderNode(child, level + 1, key, childIndex)
              )}
            </div>
          )}
          {hasChildren && isTopSection ? (
            <div>
              {item.children.map((child, childIndex) =>
                renderNode(child, level + 1, key, childIndex)
              )}
            </div>
          ) : null}
        </div>
      );
    },
    [
      expandedMenu,
      getItemKey,
      handleListItemClick,
      hasSelectedDescendant,
      isItemSelected,
      toggleGroup,
    ]
  );

  const renderMainMenu = useMemo(() => {
    return effectiveMenuItems.map((item, index) => {
      const hasNextSection = effectiveMenuItems
        .slice(index + 1)
        .some((nextItem) => Boolean(nextItem?.isSection));
      const shouldRenderDivider = Boolean(item?.isSection) && hasNextSection;

      return (
        <React.Fragment key={`menu-item-${index}`}>
          {renderNode(item, 0, "root", index)}
          {shouldRenderDivider ? (
            <div className="mx-3 my-3 h-px bg-[#555555]" />
          ) : null}
        </React.Fragment>
      );
    });
  }, [effectiveMenuItems, renderNode]);

  const collapsedRailItems = useMemo(() => {
    const iconItems = [];
    const walk = (items, depth = 0) => {
      items?.forEach((item) => {
        if ((item?.showIcon || item?.iconImage) && depth <= 1) {
          iconItems.push(item);
        }
        if (item?.children?.length && depth < 1) {
          walk(item.children, depth + 1);
        }
      });
    };
    walk(effectiveMenuItems);
    return iconItems;
  }, [effectiveMenuItems]);

  const isSettingsSelected = isAnyRouteActive([
    "/profile/personal-information",
  ]);
  const isSupportSelected = isAnyRouteActive(["/contact-us"]);
  const isAppSelected = isAnyRouteActive(["/app"]);
  const isPodcastSelected = isAnyRouteActive(["/podcast", "/podcasts"]);

  return (
    <>
      {isOverlayDrawer && isSideDrawerOpen && (
        <button
          type="button"
          aria-label="Close drawer backdrop"
          className="fixed inset-0 z-[1201] bg-[#0A1021A6] backdrop-blur-[2px]"
          onClick={() => setSideDrawerOpen(false)}
        />
      )}
      <aside
        className={`fixed left-0 top-0 z-[1202] h-full bg-black text-white transition-[width,transform] duration-200 ${
          isMobile
            ? isSideDrawerOpen
              ? "translate-x-0 pointer-events-auto"
              : "-translate-x-full pointer-events-none"
            : "translate-x-0 pointer-events-auto"
        }`}
        style={{ width: currentDrawerWidth }}
      >
        <div className="flex h-full flex-col pb-[18px]">
          {isDesktop && !isSideDrawerOpen ? (
            <div className="flex h-full flex-col items-center py-3">
              <button
                type="button"
                aria-label="Open side menu"
                className="mb-4 inline-flex h-8 w-8 items-center justify-center border border-[#2F3035] bg-[#2F3035] text-white"
                onClick={() => setSideDrawerOpen(true)}
              >
                ❯
              </button>
              <div className="mb-3 h-px w-8 bg-[#555555]" />
              <div
                className={`flex flex-1 flex-col items-center gap-4 ${DRAWER_SCROLL_NO_VISIBLE_BAR}`}
              >
                {collapsedRailItems.map((item, index) => {
                  const iconSrc =
                    item?.iconImage ||
                    MENU_SECTION_ICONS[item?.label] ||
                    placeholderIcon;
                  const isItemActive =
                    isItemSelected(item) || hasSelectedDescendant(item);
                  return (
                    <button
                      type="button"
                      key={`${item?.label || "item"}-${index}`}
                      className={`inline-flex h-8 w-8 items-center justify-center ${
                        isItemActive ? "bg-[#191919]" : ""
                      }`}
                      onClick={() => setSideDrawerOpen(true)}
                      title={item?.label}
                    >
                      <img
                        src={iconSrc}
                        alt=""
                        className="h-4 w-4 object-contain flex-shrink-0"
                      />
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 h-px w-8 bg-[#555555]" />
            </div>
          ) : (
            <>
              <div className="relative flex items-center justify-between px-3 pb-2 pt-[18px]">
                <button
                  type="button"
                  className="w-auto max-w-[calc(100%-16px)] cursor-pointer flex items-center gap-2"
                  onClick={() => {
                    window.location.href = "https://spiking.com";
                    setSideDrawerOpen(false);
                  }}
                >
                  <div className="flex-1">
                    <img
                      src={`${process.env.PUBLIC_URL}/250/s-masterclass-logo-dark.png`}
                      alt="Spiking"
                      className="h-9 w-auto max-w-full object-contain"
                    />
                  </div>
                </button>
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setSideDrawerOpen(!isSideDrawerOpen)}
                  className="absolute right-0 top-[18px] inline-flex h-6 w-6 items-center justify-center text-white/90 hover:text-white"
                  data-test-id="expand-collapse-arrow-button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 18 11"
                    className="size-4 shrink-0 fill-white rotate-90"
                  >
                    <path d="M2.1 0 9 6.84 15.9 0 18 2.08 9 11 0 2.08z" />
                  </svg>
                </button>
              </div>

              {isToken() && (
                <div className="px-3 py-3 mb-2  bg-[#191919]">
                  <div className="flex items-center gap-1">
                    {user?.ImageUrl ? (
                      <img
                        src={user.ImageUrl}
                        alt={
                          `${user?.Firstname || ""} ${
                            user?.Lastname || ""
                          }`.trim() || "User"
                        }
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#2F3035] text-sm font-semibold uppercase text-white">
                        {`${user?.Firstname?.[0] || ""}${
                          user?.Lastname?.[0] || ""
                        }` || "U"}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="truncate text-[15px] font-semibold text-white">
                        {user
                          ? `${user.Firstname || ""} ${
                              user.Lastname || ""
                            }`.trim()
                          : "Free User"}
                      </div>
                      <div className="mt-0.5 truncate text-[11px] text-[#B9C0CC]">
                        {formatMembershipLabel(
                          user?.MembershipText,
                          user?.CreatedAt
                        )}
                      </div>
                    </div>
                  </div>

                  {user?.UpgradeOrRenewButtonText &&
                  !user?.IsCenturion &&
                  !hasPaidMembership ? (
                    <button
                      type="button"
                      className="mt-3 w-full rounded-md bg-[#0D8BFF] px-3 py-2 text-[12px] font-semibold text-white transition-colors hover:bg-[#0675E8]"
                      onClick={handleUpgradeRenewClick}
                    >
                      {!isCordova()
                        ? user?.UpgradeOrRenewButtonText
                        : "Upgrade Now"}
                    </button>
                  ) : null}
                </div>
              )}

              <div
                className={`flex min-h-0 flex-1 flex-col ${DRAWER_SCROLL_NO_VISIBLE_BAR}`}
              >
                <div className="min-h-0 flex-1 px-0 pb-4 pt-3">
                  {renderMainMenu}

                  <div className="mt-1 px-0">
                    <div className="mx-3 my-3 h-px bg-[#555555]" />
                    <button
                      type="button"
                      className={`relative flex w-full items-center gap-2 px-3 py-[5px] text-left hover:bg-[#111111] ${
                        isSettingsSelected ? "bg-[#191919]" : ""
                      }`}
                      onClick={() =>
                        history.push("/profile/personal-information")
                      }
                    >
                      {isSettingsSelected ? (
                        <span className="absolute left-0 top-0 h-full w-1 bg-[#FFC337]" />
                      ) : null}
                      <img
                        src={settingIcon}
                        alt=""
                        className="h-4 w-4 object-contain"
                      />
                      <span
                        style={baseMenuTextStyle}
                        className={isSettingsSelected ? "text-[#FFC337]" : ""}
                      >
                        Setting
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`relative flex w-full items-center gap-2 px-3 py-[5px] text-left hover:bg-[#111111] ${
                        isSupportSelected ? "bg-[#191919]" : ""
                      }`}
                      onClick={() =>
                        handleListItemClick({
                          to: "https://spiking.com/contact-us",
                          isHref: true,
                          id: "support",
                        })
                      }
                    >
                      {isSupportSelected ? (
                        <span className="absolute left-0 top-0 h-full w-1 bg-[#FFC337]" />
                      ) : null}
                      <img
                        src={supportIcon}
                        alt=""
                        className="h-4 w-4 object-contain"
                      />
                      <span
                        style={baseMenuTextStyle}
                        className={isSupportSelected ? "text-[#FFC337]" : ""}
                      >
                        <FormattedMessage id="nav.support" />
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`relative flex w-full items-center gap-2 px-3 py-[5px] text-left hover:bg-[#111111] ${
                        isAppSelected ? "bg-[#191919]" : ""
                      }`}
                      onClick={handleOpenApp}
                    >
                      {isAppSelected ? (
                        <span className="absolute left-0 top-0 h-full w-1 bg-[#FFC337]" />
                      ) : null}
                      <img
                        src={appIcon}
                        alt=""
                        className="h-4 w-4 object-contain"
                      />
                      <span
                        style={baseMenuTextStyle}
                        className={isAppSelected ? "text-[#FFC337]" : ""}
                      >
                        App
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`relative flex w-full items-center gap-2 px-3 py-[5px] text-left hover:bg-[#111111] ${
                        isPodcastSelected ? "bg-[#191919]" : ""
                      }`}
                      onClick={() =>
                        handleListItemClick({
                          to: "https://spiking.com/podcast",
                          isHref: true,
                          id: "podcast",
                        })
                      }
                    >
                      {isPodcastSelected ? (
                        <span className="absolute left-0 top-0 h-full w-1 bg-[#FFC337]" />
                      ) : null}
                      <img
                        src={podcastsIcon}
                        alt=""
                        className="h-4 w-4 object-contain"
                      />
                      <span
                        style={baseMenuTextStyle}
                        className={isPodcastSelected ? "text-[#FFC337]" : ""}
                      >
                        Podcasts
                      </span>
                    </button>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-[5px] text-left hover:bg-[#111111]"
                      onClick={handleLogout}
                    >
                      <img
                        src={logoutIcon}
                        alt=""
                        className="h-4 w-4 object-contain"
                      />
                      <span style={baseMenuTextStyle}>Logout</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

export default SideDrawerNew;
